import {
  Alert,
  Badge,
  Card,
  Center,
  Divider,
  Grid,
  Group,
  Paper,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBrandMeta,
  IconInfoCircle,
  IconScale,
  IconShoppingCart,
  IconTargetArrow,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DashboardsCommon from '../../../components/DashboardsCommon';
import ErrorMessage from '../../../components/ErrorMessage';
import Spinner from '../../../components/Spinner';
import { dateToEndOfDay, dateToStartOfDay } from '../../../helpers/dateUtils';
import useFetch from '../../../helpers/useFetch';

// Meta's published EMQ quality bands (composite_score out of 10).
const emqColor = (score) => {
  if (score === null || score === undefined) return 'gray';
  if (score < 4) return 'red';
  if (score < 6) return 'yellow';
  if (score < 8) return 'blue';
  return 'green';
};
const emqBandLabel = (score) => {
  if (score === null || score === undefined) return '—';
  if (score < 4) return 'Poor';
  if (score < 6) return 'OK';
  if (score < 8) return 'Good';
  return 'Great';
};

// Coverage colour for the match-rate progress bars — low PII coverage is the
// signal we care about, so make it visually obvious.
const coverageColor = (value) => {
  if (value === null || value === undefined) return 'gray';
  if (value < 10) return 'red';
  if (value < 30) return 'yellow';
  return 'teal';
};

const fmtPct = (value) => (value === null || value === undefined ? '—' : `${value.toFixed(1)}%`);
const fmtScore = (value) => (value === null || value === undefined ? '—' : value.toFixed(1));

// Short hover descriptions for each metric, kept in one place.
const TIPS = {
  emq: "Meta's Event Match Quality (0–10): how well the customer data sent on this event matches to a Facebook user. Higher = better attribution. Bands: <4 Poor, 4–6 OK, 6–8 Good, 8+ Great.",
  emailMatch: 'Share of events that carried a usable (hashed) email address. Higher coverage feeds a higher EMQ.',
  phoneMatch: 'Share of events that carried a usable (hashed) phone number. Higher coverage feeds a higher EMQ.',
  divergence:
    'Gap between the purchases Meta’s pixel received (its own count, across all sources — browser + server-side) and the purchases Releva extracted: |Pixel − Releva| / Releva. A large gap means Meta’s view and ours disagree (a tracking/delivery issue). Alerts fire above the threshold, only for domains actively sending server-side.',
  purchases:
    'Purchases Meta’s pixel received (Meta’s own count, all sources) vs all purchases Releva extracted, for the period. A close match means tracking agrees end-to-end. “Ads” is the secondary Ads-Insights figure — purchases Meta attributed to Releva campaigns only (not all account purchases).',
  emqTrend:
    'Event Match Quality (0–10) per event type, plotted daily. A line trending down means matching is degrading for that event (e.g. fewer events carrying email/phone, or a hashing regression).',
  emailMatchTrend:
    'Hashed-email coverage (%) per event type over time. Drops here pull EMQ down and weaken attribution.',
  purchasesTrend:
    'Daily purchase counts: Pixel-received (Meta’s own pixel count, all sources) vs Releva-extracted (Releva’s own events), plus the Ad-attributed (Releva campaigns) figure. A persistent gap between pixel-received and Releva-extracted is attribution divergence.',
  purchasesCustomer:
    'Releva-extracted purchases for the period (and the Ad-attributed figure for Releva campaigns). The pixel-received comparison isn’t shown because Releva isn’t sending server-side for this client — the metrics above reflect their own Meta integration.',
  integrationManaged:
    'Releva sends this client’s events server-side via the Conversions API. EMQ, match rate, and the pixel-received vs Releva purchase comparison all reflect Releva-managed delivery.',
  integrationCustomer:
    'Backend (server-side) tracking is off, so Releva isn’t sending events for this client. These metrics reflect the client’s OWN Meta pixel integration — use them to surface issues in the customer’s setup.',
};

// Info icon with a hover tooltip. Wrapped in Center so it forwards a ref to Tooltip.
const InfoDot = ({ tip }) => (
  <Tooltip label={tip} multiline w={270} withArrow position="top" events={{ hover: true, focus: true, touch: true }}>
    <Center component="span" style={{ cursor: 'help', color: 'var(--mantine-color-gray-5)' }}>
      <IconInfoCircle size={13} />
    </Center>
  </Tooltip>
);

const MetricCard = ({ icon, color, label, value, valueColor, hint, tip }) => (
  <Paper withBorder radius="md" p="md">
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <div>
        <Group gap={4} wrap="nowrap">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {label}
          </Text>
          {tip && <InfoDot tip={tip} />}
        </Group>
        <Text fz={26} fw={700} c={valueColor} mt={4} lh={1.1}>
          {value}
        </Text>
        {hint && (
          <Text size="xs" c="dimmed" mt={4}>
            {hint}
          </Text>
        )}
      </div>
      <ThemeIcon variant="light" color={color} size={40} radius="md">
        {icon}
      </ThemeIcon>
    </Group>
  </Paper>
);

const DataQualityTab = ({ startDate, endDate }) => {
  const fetch = useFetch();
  const { domain } = useSelector((state) => state.domain);

  const [data, setData] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    let ignore = false;
    setData(undefined);
    setError(undefined);

    const startTs = dateToStartOfDay(startDate, 'local');
    const endTs = dateToEndOfDay(endDate, 'local');

    fetch(
      `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/facebook/data-quality/${encodeURIComponent(
        startTs
      )}/${encodeURIComponent(endTs)}`
    )
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (!ignore) setError(err);
      });

    return () => {
      ignore = true;
    };
  }, [domain.accountId, domain.id, startDate, endDate, fetch]);

  if (error) {
    return <ErrorMessage message={error.message || 'Failed to load data-quality metrics'} />;
  }

  if (data === undefined) {
    return <Spinner />;
  }

  // Section header — this tab currently surfaces Facebook (CAPI) event quality;
  // future iterations will add more sources (Google, WhatsApp, products, sync, …)
  // as their own sections below.
  const sectionHeader = (
    <>
      <Group gap="sm" mt="md" wrap="nowrap">
        <ThemeIcon variant="light" color="blue" size={42} radius="md">
          <IconBrandMeta size={24} />
        </ThemeIcon>
        <div>
          <Group gap="xs">
            <Title order={4}>Facebook events</Title>
            {data.pixelId && (
              <Badge variant="light" color="gray" size="sm">
                Pixel {data.pixelId}
              </Badge>
            )}
            {data.configured && (
              <Tooltip
                label={data.backendTracking ? TIPS.integrationManaged : TIPS.integrationCustomer}
                multiline
                w={270}
                withArrow
                position="top"
                events={{ hover: true, focus: true, touch: true }}
              >
                <Badge variant="light" color={data.backendTracking ? 'blue' : 'gray'} size="sm" style={{ cursor: 'help' }}>
                  {data.backendTracking ? 'Releva-managed' : 'Customer integration'}
                </Badge>
              </Tooltip>
            )}
          </Group>
        </div>
      </Group>
      <Divider mb="md" />
    </>
  );

  if (!data.configured) {
    return (
      <>
        {sectionHeader}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="No Facebook pixel configured">
          No Meta pixel is configured for this domain, so there are no data-quality metrics to show. Add a pixel in the
          Facebook settings (and enable backend tracking to send purchases server-side via the Conversions API).
        </Alert>
      </>
    );
  }

  // The zeroEvents alert above is non-blocking, so execution still reaches the
  // mapping below — default totals/events so a zeroEvents response (or any
  // partial payload) can't throw on `events.map`.
  const { totals = {}, events = [] } = data;
  const emqSeries = events.map((event) => ({ id: event, data: data.emq?.[event] || [] }));
  const matchRateSeries = events.map((event) => ({ id: event, data: data.matchRate?.email?.[event] || [] }));

  const purchaseEmq = totals.emq?.Purchase ?? null;
  // Divergence is only meaningful when Releva sends server-side. Force it to null for a
  // customer-managed integration so a stale historical value (from when the domain was
  // managed) can't show a coloured % that contradicts the "Customer integration" badge.
  const divergencePct =
    !data.backendTracking || totals.attributionDivergence == null ? null : totals.attributionDivergence * 100;
  const divergenceThreshold = data.thresholds?.divergencePct ?? 20;
  const divergent = divergencePct !== null && divergencePct > divergenceThreshold;

  // Pixel-received only carries signal when Releva sends server-side; for a
  // customer-managed integration it's a flat zero, so drop that line.
  const purchaseTrendStats = [
    ...(data.backendTracking ? [{ id: 'Pixel-received', data: data.pixelReceivedPurchases || [] }] : []),
    { id: 'Releva-extracted', data: data.relevaExtractedPurchases || [] },
    { id: 'Ad-attributed (Releva campaigns)', data: data.adAttributedPurchases || [] },
  ];
  const purchaseTrendTitle = data.backendTracking
    ? 'Purchases: Pixel-received vs Releva-extracted vs Ad-attributed'
    : 'Purchases: Releva-extracted vs Ad-attributed';

  return (
    <Stack gap="md">
      {sectionHeader}

      {!data.backendTracking && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light" title="Customer-managed integration">
          Server-side (Conversions API) tracking is off, so Releva isn’t sending events for this client. The
          match-quality metrics below reflect the client’s own Meta pixel integration — use them to surface issues in
          their setup. The pixel-received purchase comparison applies only when Releva sends server-side.
        </Alert>
      )}

      {data.zeroEvents && (
        <Alert icon={<IconAlertTriangle size={16} />} color="orange" title="No event-match data">
          Meta reported no event-match data for pixel {data.pixelId} in the selected period (EMQ and match-rate are
          unavailable). For a domain sending server-side this can mean events stopped flowing; the purchase comparison
          below still reflects what was received.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <Paper withBorder radius="md" p="md">
          <Group wrap="nowrap">
            <RingProgress
              size={76}
              thickness={7}
              roundCaps
              sections={[{ value: purchaseEmq === null ? 0 : (purchaseEmq / 10) * 100, color: emqColor(purchaseEmq) }]}
              label={
                <Text ta="center" fw={700} size="sm">
                  {fmtScore(purchaseEmq)}
                </Text>
              }
            />
            <div>
              <Group gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  EMQ · Purchase
                </Text>
                <InfoDot tip={TIPS.emq} />
              </Group>
              <Badge color={emqColor(purchaseEmq)} variant="light" mt={6}>
                {emqBandLabel(purchaseEmq)}
              </Badge>
              <Text size="xs" c="dimmed" mt={4}>
                out of 10
              </Text>
            </div>
          </Group>
        </Paper>

        <MetricCard
          icon={<IconTargetArrow size={22} />}
          color="teal"
          label="Email match · Purchase"
          value={fmtPct(totals.matchRateEmail?.Purchase)}
          hint="Hashed email coverage"
          tip={TIPS.emailMatch}
        />

        <MetricCard
          icon={<IconScale size={22} />}
          color={divergencePct === null ? 'gray' : divergent ? 'red' : 'green'}
          label="Attribution divergence"
          value={fmtPct(divergencePct)}
          valueColor={divergent ? 'red' : undefined}
          hint={
            divergencePct === null
              ? data.backendTracking
                ? 'No baseline yet'
                : 'Server-side tracking off'
              : divergent
              ? `Above ${divergenceThreshold}% threshold`
              : 'Within tolerance'
          }
          tip={TIPS.divergence}
        />

        {data.backendTracking ? (
          <MetricCard
            icon={<IconShoppingCart size={22} />}
            color="grape"
            label="Purchases · Pixel vs Releva"
            value={`${totals.pixelReceivedPurchases ?? 0} / ${totals.relevaExtractedPurchases ?? 0}`}
            hint={`Pixel-received / Releva-extracted · Ads: ${totals.adAttributedPurchases ?? 0}`}
            tip={TIPS.purchases}
          />
        ) : (
          <MetricCard
            icon={<IconShoppingCart size={22} />}
            color="grape"
            label="Purchases · Releva-extracted"
            value={`${totals.relevaExtractedPurchases ?? 0}`}
            hint={`Releva-extracted · Ads: ${totals.adAttributedPurchases ?? 0}`}
            tip={TIPS.purchasesCustomer}
          />
        )}
      </SimpleGrid>

      <Card padding="md" radius="md" withBorder>
        <Title order={5} mb="md">
          Event match quality by event type
        </Title>
        <Table verticalSpacing="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Event</Table.Th>
              <Table.Th>
                <Group gap={4} wrap="nowrap">
                  EMQ <InfoDot tip={TIPS.emq} />
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={4} wrap="nowrap">
                  Email match rate <InfoDot tip={TIPS.emailMatch} />
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={4} wrap="nowrap">
                  Phone match rate <InfoDot tip={TIPS.phoneMatch} />
                </Group>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {events.map((event) => {
              const email = totals.matchRateEmail?.[event];
              const phone = totals.matchRatePhone?.[event];
              return (
                <Table.Tr key={event}>
                  <Table.Td>
                    <Text fw={500}>{event}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={emqColor(totals.emq?.[event])} variant="light">
                      {fmtScore(totals.emq?.[event])} · {emqBandLabel(totals.emq?.[event])}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Progress value={email ?? 0} w={120} radius="xl" color={coverageColor(email)} />
                      <Text size="xs" w={46} ta="right" c="dimmed">
                        {fmtPct(email)}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Progress value={phone ?? 0} w={120} radius="xl" color={coverageColor(phone)} />
                      <Text size="xs" w={46} ta="right" c="dimmed">
                        {fmtPct(phone)}
                      </Text>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <DashboardsCommon
            stats={emqSeries}
            chartTitle={
              <>
                Event Match Quality (EMQ) trend <InfoDot tip={TIPS.emqTrend} />
              </>
            }
            lineStacked={false}
            expand={true}
            type={'card'}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <DashboardsCommon
            stats={matchRateSeries}
            chartTitle={
              <>
                Email match rate trend (%) <InfoDot tip={TIPS.emailMatchTrend} />
              </>
            }
            lineStacked={false}
            expand={true}
            type={'card'}
          />
        </Grid.Col>
      </Grid>

      <DashboardsCommon
        stats={purchaseTrendStats}
        chartTitle={
          <>
            {purchaseTrendTitle} <InfoDot tip={TIPS.purchasesTrend} />
          </>
        }
        lineStacked={false}
        expand={false}
        type={'card'}
      />
    </Stack>
  );
};

export default DataQualityTab;
